import { fetchJSON as fetchAPI } from './client';

export type KPICategory = {
  category: string;
  name: string;
  count: number;
};

export type KPIDefinition = {
  key: string;
  name: string;
  category: string;
  business_definition: string;
  formula: string;
  data_sources: string[];
  refresh_frequency: string;
  dimensions: string[];
  owner: string;
  expected_range?: string;
  alert_threshold?: string;
  business_meaning?: string;
  ai_usage?: string;
  unit?: string;
  improvement_strategy?: string;
  expected_business_impact?: string;
  dependencies?: string[];
  experiment_support: boolean;
};

export type KPIPhilosophyField = {
  key: string;
  name: string;
  order: number;
};

export type HealthScoreMethodology = {
  key: string;
  name: string;
  formula: string;
  weightage: number;
  business_interpretation: string;
  priority: string;
  suggested_actions?: string[];
};

export type ExperimentKPIField = {
  key: string;
  name: string;
  description: string;
  required: boolean;
};

export type AttributionKPI = {
  key: string;
  name: string;
  description?: string;
  ai_method?: string;
};

export type ExecutiveDashboard = {
  key: string;
  name: string;
  kpis: string[];
  description?: string;
};

export type BenchmarkType = {
  key: string;
  name: string;
  description: string;
};

export type AIConsumptionRule = {
  key: string;
  name: string;
  description: string;
  mandatory: boolean;
};

export type SemanticMapping = {
  kpi_key: string;
  semantic_layer: string;
  knowledge_graph: boolean;
  marketplace_dna: boolean;
  recommendation_engine: boolean;
  experiment_engine: boolean;
  executive_cockpit: boolean;
};

export type KPIValidationResult = {
  kpi_key: string;
  valid: boolean;
  registered: boolean;
  gaps?: string[];
  evaluated_at: string;
};

export type KPIBibleDashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  depends_on: string[];
  total_kpis: number;
  categories: number;
  health_scores: number;
  executive_views: number;
  semantic_mappings: number;
  category_breakdown: KPICategory[];
  generated_at: string;
};

export type KPIBibleStatus = {
  bible_id: string;
  version: string;
  depends_on: string[];
  total_kpis: number;
  categories: number;
  health_scores: number;
  executive_views: number;
  semantic_mappings: number;
  authoritative: boolean;
  status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const marketplaceKPIBibleApi = {
  dashboard: () => fetchMarketIntel<KPIBibleDashboard>('/kpi-bible/dashboard'),
  philosophy: () => fetchMarketIntel<{ fields: KPIPhilosophyField[] }>('/kpi-bible/philosophy').then((r) => r.fields),
  categories: () => fetchMarketIntel<{ categories: KPICategory[] }>('/kpi-bible/categories').then((r) => r.categories),
  dictionary: (category?: string) =>
    fetchMarketIntel<{ kpis: KPIDefinition[]; count: number }>(
      category ? `/kpi-bible/dictionary?category=${encodeURIComponent(category)}` : '/kpi-bible/dictionary',
    ).then((r) => r.kpis),
  getKPI: (key: string) => fetchMarketIntel<KPIDefinition>(`/kpi-bible/dictionary/${key}`),
  healthMethodology: () =>
    fetchMarketIntel<{ methodology: HealthScoreMethodology[] }>('/kpi-bible/health-methodology').then((r) => r.methodology),
  experimentKPIs: () => fetchMarketIntel<{ fields: ExperimentKPIField[] }>('/kpi-bible/experiment-kpis').then((r) => r.fields),
  attributionKPIs: () =>
    fetchMarketIntel<{ attribution_kpis: AttributionKPI[] }>('/kpi-bible/attribution-kpis').then((r) => r.attribution_kpis),
  executiveDashboards: () =>
    fetchMarketIntel<{ dashboards: ExecutiveDashboard[] }>('/kpi-bible/executive-dashboards').then((r) => r.dashboards),
  benchmarks: () => fetchMarketIntel<{ benchmarks: BenchmarkType[] }>('/kpi-bible/benchmarks').then((r) => r.benchmarks),
  aiConsumptionRules: () => fetchMarketIntel<{ rules: AIConsumptionRule[] }>('/kpi-bible/ai-consumption-rules').then((r) => r.rules),
  semanticMappings: () =>
    fetchMarketIntel<{ mappings: SemanticMapping[]; count: number }>('/kpi-bible/semantic-mappings').then((r) => r.mappings),
  validate: (kpiKey: string) =>
    fetchMarketIntel<KPIValidationResult>('/kpi-bible/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kpi_key: kpiKey }),
    }),
  status: () => fetchMarketIntel<KPIBibleStatus>('/kpi-bible/status'),
};
